//skip
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, InputAdornment, TextField } from "@mui/material";
import { ButtonGroup, LinearProgress, Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@material-ui/icons/Clear";
import { blue, red } from "@material-ui/core/colors";
import RegularButton from "components/CustomButtons/Button";
import { useForm } from "hooks/useForm";
import { useFixDate } from "hooks/useFixDate";
import { useFetchLocal } from "hooks/fetch-hooks/useFetchLocal";
import {
  useCheckInventoryStatus,
  LIQUIDATION_INVENTORY_CLOSED_MESSAGE,
} from "hooks/fetch-hooks/useCheckInventoryStatus";
import { formatCustomDate, fixTime } from "helpers/dateTools";
import { getDecimalFormat } from "helpers/getNumberFormats";
import { validateObject } from "helpers/validateObject";
import { resetOnClick } from "helpers/resetOnClick";
import { liquidationStatus, unitsTypesObj } from "helpers/dictionary";
import {
  createLiquidationStationSale,
  createSgcStationSaleSgc,
  resetCustomersOptions,
} from "redux/actions/liquidations/liquidationsAction";
import { setSnackbar } from "redux/actions/search/searchActions";
import { getSgc } from "redux/actions/sgc/sgcActions";
import {
  handleFetchSalesAccountDetails,
  handleUltimateFetchLessData,
} from "views/Closeouts-Settlements/actions/fetchData";
import { fetchWholesaleUnits, fetchUnitRetention } from "./actions/fetchWholesaleUnits";
import { initialValues } from "./initialValues";
import { orderArrayV2 } from "helpers/orderArray";
import { formatNumber } from "helpers/getNumberFormats";
import { BlockCreditMessage } from "./BlockCreditMessage";
import { FORM_STRINGS } from "./liquidationFormLabels";
import { newDate } from "helpers/dateTools";

export const LiquidationForm = ({
  onClose,
  readOnly = false,
  liquidation = {},
  isNew = false,
}) => {
  const dispatch = useDispatch();
  const { payments, accounts, station: stationFilter } = useSelector(
    ({ liquidations }) => liquidations,
  );
  const { products } = useSelector(({ routesStatus }) => routesStatus);
  const { stations, stationsList } = useSelector(({ companies }) => companies);
  const { unitsMap, units } = useSelector(({ unitsReducer }) => unitsReducer);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const { sgc } = useSelector(({ sgc }) => sgc);
  const validation =
    readOnly &&
    liquidation?.fillingOrder !== null &&
    liquidation?.fillingOrder?.length
      ? true
      : false;

  const condonationVal = liquidation.status === liquidationStatus.value.SAVED;
  const {
    inputValues,
    fieldErrors,
    handleInputOnChange,
    handleNewValues,
    runValidates,
    onChangeNumber,
  } = useForm(initialValues.values, initialValues.required);

  const {
    date,
    idStation,
    idUnit,
    unitsOpt,
    searchCustomer,
    idAccount,
    customersOpt,
    productsOpt,
    product,
    cil10,
    cil20,
    cil30,
    cil45,
    kgs,
    lts,
    total,
    unitPriceKg,
    unitPriceLt,
    tax,
    discount,
    idPayment,
    paymentsOpt,
    sgcSelected,
    totalByProduct,
    blockCredit,
    withholding,
    retentionsTotal,
    efe
  } = inputValues;

  const {
    ESTACIONARIO, PORTATIL
  } = unitsTypesObj

  const {
    search, payment, unitTypes,
    labels,
    placeholders, noOptions, buttons, messages, unitMeasure
  } = FORM_STRINGS

  // La planta de la venta se define al elegir la cuenta. Mientras no exista,
  // se valida contra la planta seleccionada en el filtro de la vista para dar
  // retroalimentación desde el primer cambio de fecha; al llegar la cuenta se
  // revalida con su planta.
  const idStationToValidate = idStation?.id || stationFilter?.id;

  const {
    isSuccess,
    errorInv,
    loading: loadingInv,
    message: messageInv
  } = useCheckInventoryStatus(idStationToValidate, date, LIQUIDATION_INVENTORY_CLOSED_MESSAGE);

  useFixDate({ date, action: handleNewValues });

  const [
    [_, loadingCustomers, success, _e, message],
    fetchCustomers,
  ] = useFetchLocal(handleUltimateFetchLessData);

  const [
    [detailsResponse, loadingDetails, successDetails],
    fetchCustomerDetails,
  ] = useFetchLocal(handleFetchSalesAccountDetails);

  const [
    [wholesaleUnitsResponse, loadingWholesaleUnits],
    fetchWholesaleUnitsFn,
  ] = useFetchLocal(fetchWholesaleUnits);

  const [
    [unitRetentionResponse],
    fetchUnitRetentionFn,
  ] = useFetchLocal(fetchUnitRetention);

  // Aviso derivado: explica por qué una cuenta no mayorista queda sin unidades ni retenciones
  const notWholesalerMessage =
    idAccount && successDetails && !detailsResponse?.isWholesaler
      ? messages.notWholesaler
      : "";

  const handleSearchCustomers = (e) => {
    e.preventDefault();
    dispatch(resetCustomersOptions());

    fetchCustomers(stationsList, searchCustomer, stations, handleNewValues);
  };

  const filterUnits = useCallback(
    (id) => {
      return (
        unitsMap.filter(
          (unit) =>
            (unit.idStation === id &&
              unit.type.includes(unitTypes.mayor)) ||
            (unit.idStation === id && unit.type.includes(unitTypes.anden))
        ) || []
      );
    },
    [unitsMap],
  );

  const handleFetchPublic = () => {
    handleNewValues({ searchCustomer: search.public, idAccount: null });
    fetchCustomers(stationsList, search.publicGeneral, stations, handleNewValues);
  };

  const handleAccount = useCallback((v) => {
    const value = v ? v : null;
    const stationSelected = stations[(value?.idStation)] || null;
    const isPublicCode =
      value?.customerCode === "999" || value?.customerCode === 999;
    const units =
      typeof value === "object" ? filterUnits(stationSelected?.id) : [];
    let unitsF = [];
    let productsF = [];

    if (v !== null || (v !== undefined && validateObject(value))) {
      if (
        value.accountType === PORTATIL
      ) {
        unitsF = units.filter(
          (unit) =>
            unit.type.includes(unitTypes.anden) ||
            unit.type.includes(unitTypes.p)
        );
      } else if (value?.accountType === ESTACIONARIO) {
        unitsF = units.filter((unit) => unit.type.includes(unitTypes.e));
      }

      if (
        unitsF[0]?.type?.includes(unitTypes.p) ||
        unitsF[0]?.type?.includes(unitTypes.anden)
      ) {
        productsF = products.filter((product) =>
          product.type.includes(PORTATIL)
        );
      } else {
        productsF = products.filter((product) =>
          product.type.includes(ESTACIONARIO)
        );
      }

      handleNewValues({
        idAccount: value,
        idStation: stations[(value?.idStation)] || null,
        cil10: 0,
        cil20: 0,
        cil30: 0,
        cil45: 0,
        lts: 0,
        kgs: 0,
        kgsCil: 0,
        quantity: 0,
        total: 0,
        totalByProduct: 0,
        unitPriceKg: 0,
        unitPriceLt: 0,
        tax: 0,
        discount: 0,
        withholding: 0,
        efe: 0,
        retentionsTotal: 0,
        balance: 0,
        blockCredit: false,
        idPayment: null,
        paymentsOpt: [],
        sgcSelected: null,
        unitsOpt: unitsF,
        idUnit: unitsF?.length === 1 ? unitsF[0] : null,
        searchCustomer: value?.alias || "",
        productsOpt: productsF,
        product: productsF?.length === 1 ? productsF[0] : null,
      });
    }
  }, []);

  const handleProduct = (v) => {
    const value = v ? v : null;
    handleNewValues({
      product: value,
    });
  };
  const handleMethodPayment = (e, v) => {
    const value = v ? v : null;
    handleNewValues({
      idPayment: value,
    });
  };

  const handleSgc = (v) => {
    const value = v ? v : null;
    handleNewValues({
      sgcSelected: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (runValidates(["sgcSelected"])) return;
    //eslint-disable-next-line
    const {
      customersOpt: _customersOpt,
      unitsOpt: _unitsOpt,
      searchCustomer: _searchCustomer,
      sgcSelected: _sgcSelected,
      product: _product,
      quantity: _quantity,
      productsOpt: _productsOpt,
      paymentsOpt: _paymentsOpt,
      totalByProduct,
      ...rest
    } = inputValues;

    const form = {
      ...rest,
      date: fixTime(date),
      idAccount: idAccount.idAccount || liquidation.idAccount,
      idStation: idStation.id,
      idUnit: idUnit?.id,
      idPayment: idPayment.id,
      cil10: parseInt(cil10) || 0,
      cil20: parseInt(cil20) || 0,
      cil30: parseInt(cil30) || 0,
      cil45: parseInt(cil45) || 0,
      lts: parseFloat(lts) || 0,
      total: parseFloat(total) || 0,
      kgs: parseFloat(kgs) || 0,
      unitPriceKg: parseFloat(unitPriceKg) || 0,
      unitPriceLt: parseFloat(unitPriceLt) || 0,
      tax: parseFloat(tax) || 0,
      discount: parseFloat(discount) || 0,
      unitPrice: unitPriceKg || unitPriceLt,
      efe: parseFloat(efe) || 0,
      sgc: !idPayment?.label?.includes(payment.condon) ? sgcSelected?.name : payment.sgcSkip,
    };

    dispatch(createLiquidationStationSale(form, onClose));
  };

  const handleSgcSubmit = () => {
    dispatch(
      createSgcStationSaleSgc(
        {
          sgc: sgcSelected?.name,
          id: liquidation?.id,
          idStation: liquidation?.idStation,
        },
        onClose,
      ),
    );
  };

  useEffect(() => {
    searchCustomer?.length === 0 && handleNewValues({ idAccount: null });
  }, [searchCustomer]);

  useEffect(() => {
    if (readOnly) {
      const liquidationValues = {
        ...liquidation,
        idPayment: payments.find((py) => py.id === liquidation?.idPayment),
        idStation: stations[liquidation.idStation],
        idUnit: units[liquidation.idUnit],
        date: formatCustomDate(liquidation.date, "-"),
        total: liquidation?.total,
        withholding: liquidation?.withholding
      };

      handleNewValues(liquidationValues);
    } else {
      handleNewValues({
        idPayment: payments.find((py) => py.label.includes(payment.efectivo)),
      });
    }
    accounts?.length > 0 && dispatch(resetCustomersOptions());
    sgc?.length === 0 && dispatch(getSgc(stationsList));
  }, []);

  useEffect(() => {
    if (successDetails) {
      const skip = ["NOTA NACIONAL", "POR DEFINIR"];
      const showCredit = detailsResponse?.isCredit === 1 && detailsResponse?.isCreditSuspended !== 1;
      let paymentsF = payments.filter((py) => {
        if (skip.includes(py.label)) return false;
        if (py.label === "CREDITO" && !showCredit) return false;
        return true;
      });
      handleNewValues({
        idPayment: paymentsF[0],
        paymentsOpt: paymentsF,
        withholding: 0,
        efe: 0,
        retentionsTotal: 0,
      });
    }
  }, [detailsResponse, idAccount]);

  useEffect(() => {
    if (!readOnly) {
      const totalKgs =
        (parseInt(cil10) || 0) * 10 +
        (parseInt(cil20) || 0) * 20 +
        (parseInt(cil30) || 0) * 30 +
        (parseInt(cil45) || 0) * 45 +
        (parseFloat(kgs) || 0);

      const isE_Unit = idUnit?.type?.includes(unitTypes.e);
      const unitSelected = isE_Unit ? unitPriceLt : unitPriceKg;
      let discountRes;

      if (detailsResponse?.isCreditNote) {
        discountRes = 0;
      } else {
        discountRes = detailsResponse?.discount || 0;
      }

      if (
        detailsResponse?.isWholesaler &&
        unitRetentionResponse?.showWithholding === true &&
        unitRetentionResponse?.withholdingPercentage > 0 &&
        unitRetentionResponse?.withholdingPercentage <= discountRes
      ) {
        discountRes = detailsResponse?.discount - unitRetentionResponse.withholdingPercentage;
      }

      let discount = isE_Unit
        ? (detailsResponse?.discount) * lts || 0
        : detailsResponse?.discount * totalKgs || 0;

      let retention = detailsResponse?.isWholesaler
        ? isE_Unit
          ? (unitRetentionResponse?.withholdingPercentage) * lts || 0
          : (unitRetentionResponse?.withholdingPercentage || 0) * totalKgs || 0
        : 0;

      const grossTotal = unitSelected * (lts || totalKgs || 0);

      const totalP =
        (product?.capacity === 10 && cil10 * product?.capacity * unitPriceKg) ||
        (product?.capacity === 20 && cil20 * product?.capacity * unitPriceKg) ||
        (product?.capacity === 30 && cil30 * product?.capacity * unitPriceKg) ||
        (product?.capacity === 45 && cil45 * product?.capacity * unitPriceKg) ||
        (product?.capacity === 1 && kgs * unitPriceKg) ||
        (product?.capacity === 1 && lts * unitPriceLt);
      
      handleNewValues({
        total: parseFloat(grossTotal - discount).toFixed(2),
        discount: parseFloat(discount).toFixed(2),
        totalByProduct: formatNumber({ input: totalP }),
        withholding: retention,
      });
    }
  }, [cil10, cil20, cil30, cil45, kgs, lts, unitPriceKg, unitPriceLt, retentionsTotal, unitRetentionResponse]);

  useEffect(() => {
    if (!idAccount?.idAccount) return;
    fetchCustomerDetails(date, idAccount.idAccount);
  }, [idAccount?.idAccount, date]);

  useEffect(() => {
    if (idUnit?.type === PORTATIL) {
      handleNewValues({
        productsOpt: products.filter((product) =>
          product.type.includes(PORTATIL)
        ),
      });
    } else if (idUnit?.type === ESTACIONARIO) {
      handleNewValues({
        productsOpt: products.filter((product) =>
          product.type.includes(ESTACIONARIO)
        ),
      });
    }
  }, [idUnit]);

  useEffect(() => {
    if (idUnit?.id && detailsResponse?.isWholesaler) {
      fetchUnitRetentionFn(idUnit.id);
    } else {
      handleNewValues({ withholding: 0, efe: 0 });
    }
  }, [idUnit?.id]);

  useEffect(() => {
    if (successDetails) {
      handleNewValues({
        unitPriceKg: detailsResponse.unitPriceKg,
        unitPriceLt: detailsResponse.unitPriceLt,
        tax: detailsResponse.tax,
      });
      if (detailsResponse.isWholesaler) {
        fetchWholesaleUnitsFn(idAccount?.idCustomer);
      } else {
        handleNewValues({ withholding: 0, efe: 0 });
      }
    }
  }, [successDetails, detailsResponse]);

  useEffect(() => {
    if (wholesaleUnitsResponse?.units?.length) {
      const wholesaleUnits = wholesaleUnitsResponse.units.map((u) => ({
        id: u.idUnit,
        value: u.idUnit,
        label: u.unitNumber,
        type: units[u.idUnit]?.type,
        idStation: units[u.idUnit]?.idStation,
      }));
      handleNewValues({
        unitsOpt: wholesaleUnits,
        idUnit: wholesaleUnits.length === 1 ? wholesaleUnits[0] : null,
      });
    } else if (wholesaleUnitsResponse?.units?.length === 0) {
      dispatch(setSnackbar("No se encontraron unidades asignadas para esta cuenta mayorista", "warning"));
    }
  }, [wholesaleUnitsResponse]);

  useEffect(() => {
    handleNewValues({
      efe: unitRetentionResponse?.efe || 0,
    });
  }, [unitRetentionResponse]);

  return (
    <Grid container spacing={1} style={{ transition: "ease" }}>
      <BlockCreditMessage block={blockCredit} />
      <Grid item xs={12}>
        <TextField
          type="date"
          label={labels.date}
          name="date"
          value={date}
          onChange={handleInputOnChange}
          variant="outlined"
          fullWidth
          disabled={readOnly}
          error={errorInv}
          helperText={
            loadingInv
              ? messages.validatingInventory
              : messageInv || ""
          }
          inputProps={{
            max: newDate()
          }}
        />
        {loadingInv && <LinearProgress />}
      </Grid>
      <Grid item xs={12} md={liquidation.status ? 12 : 9}>
        {!readOnly ? (
          <form onSubmit={(e) => handleSearchCustomers(e)}>
            <Autocomplete
              id="liquidation-customers-options"
              options={customersOpt || []}
              open={customersOpt?.length > 0 && !idAccount}
              value={idAccount}
              inputValue={searchCustomer}
              onInputChange={(e, newInputValue) =>
                handleNewValues({ searchCustomer: newInputValue })
              }
              getOptionLabel={(option) => option?.label}
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    label={
                      loadingCustomers
                        ? labels.loading
                        : success
                          ? labels.accounts
                          : labels.searchAccount
                    }
                    variant="outlined"
                    placeholder={placeholders.searchCustomer}
                    name="searchCustomer"
                    helperText={message || notWholesalerMessage}
                  />
                  <ButtonGroup
                    style={{
                      position: "absolute",
                      right: 33,
                      top: "18%",
                    }}
                  >
                    <RegularButton
                      justIcon
                      round
                      color="transparent"
                      onClick={(e) => handleSearchCustomers(e)}
                      size="sm"
                      loading={loadingCustomers}
                    >
                      <SearchIcon style={{ color: blue[800] }} />
                    </RegularButton>
                    <RegularButton
                      justIcon
                      round
                      color="transparent"
                      onClick={(_) =>
                        handleNewValues({ searchCustomer: "", idAccount: null })
                      }
                      size="sm"
                      loading={loadingCustomers}
                    >
                      <ClearIcon />
                    </RegularButton>
                  </ButtonGroup>
                </>
              )}
              disabled={loadingCustomers}
              fullWidth
              onChange={(_, v) => handleAccount(v)}
              noOptionsText={noOptions.customer}
              closeIcon={false}
              style={{
                position: "relative",
              }}
            />
          </form>
        ) : (
          <TextField
            fullWidth
            label={labels.account}
            name="accountAlias"
            value={inputValues.accountAlias}
            variant="outlined"
            disabled={readOnly}
          />
        )}
      </Grid>
      {!liquidation?.status && (
        <Grid item xs={12} md={3}>
          <RegularButton
            color="info"
            fullWidth
            loading={loadingCustomers}
            onClick={handleFetchPublic}
          >
            {buttons.publicGeneral}
          </RegularButton>
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={labels.station}
          name="idStation"
          value={idStation?.label || ""}
          variant="outlined"
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        {!readOnly ? (
          <Autocomplete
            id="liquidation-units-options"
            options={orderArrayV2(unitsOpt || [])}
            value={idUnit}
            getOptionLabel={(option) => option?.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label={labels.unit}
                variant="outlined"
                error={fieldErrors.idUnit}
                helperText={fieldErrors.idUnit || ""}
              />
            )}
            fullWidth
            onChange={(e, v) =>
              handleNewValues({
                idUnit: v,
                unitType: v?.type || "",
                cil10: 0,
                cil20: 0,
                cil30: 0,
                cil45: 0,
                lts: 0,
              })
            }
            noOptionsText={noOptions.unit}
            disabled={readOnly}
          />
        ) : (
          <TextField
            fullWidth
            label={labels.unit}
            value={idUnit?.label}
            variant="outlined"
            disabled={readOnly}
          />
        )}
      </Grid>

      {idUnit?.type?.includes(unitTypes.p) ||
        idUnit?.type?.includes(unitTypes.anden) ? (
        <>
          {!readOnly && (
            <>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  id="liquidation-products-options"
                  options={productsOpt || []}
                  value={product}
                  getOptionLabel={(option) => option?.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={labels.product}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  onChange={(e, v) => handleProduct(v)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={labels.quantity}
                  name={
                    (product?.capacity === 10 && "cil10") ||
                    (product?.capacity === 20 && "cil20") ||
                    (product?.capacity === 30 && "cil30") ||
                    (product?.capacity === 45 && "cil45") ||
                    (product?.capacity === 1 && "kgs")
                  }
                  value={
                    (product?.capacity === 10 && cil10) ||
                    (product?.capacity === 20 && cil20) ||
                    (product?.capacity === 30 && cil30) ||
                    (product?.capacity === 45 && cil45) ||
                    (product?.capacity === 1 && kgs) ||
                    ""
                  }
                  onChange={(e) =>
                    onChangeNumber(e, {
                      decimals:
                        idUnit?.type.includes(unitTypes.e) || product?.name === unitMeasure.kilos
                          ? 2
                          : 0,
                    })
                  }
                  variant="outlined"
                  fullWidth
                  inputProps={{ min: 0, right: "1" }}
                  onFocus={() =>
                    resetOnClick(
                      onChangeNumber,
                      (product?.capacity === 10 && cil10) ||
                      (product?.capacity === 20 && cil20) ||
                        (product?.capacity === 30 && cil30) ||
                        (product?.capacity === 45 && cil45) ||
                        (product?.capacity === 1 && kgs),

                      (product?.capacity === 10 && "cil10") ||
                      (product?.capacity === 20 && "cil20") ||
                        (product?.capacity === 30 && "cil30") ||
                        (product?.capacity === 45 && "cil45") ||
                        (product?.capacity === 1 && "kgs"),
                    )
                  }
                />
              </Grid>
            </>
          )}
          <Grid item xs={6} md={2}>
            <TextField
              label={labels.cil10}
              name="cil10"
              value={cil10}
              variant="outlined"
              fullWidth
              type="number"
              inputProps={{
                min: 0,
                max: 60,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label={labels.cil20}
              name="cil20"
              value={cil20}
              variant="outlined"
              fullWidth
              type="number"
              inputProps={{
                min: 0,
                max: 60,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label={labels.cil30}
              name="cil30"
              value={cil30}
              variant="outlined"
              fullWidth
              type="number"
              inputProps={{
                min: 0,
                max: 60,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              label={labels.cil45}
              name="cil45"
              value={cil45}
              variant="outlined"
              fullWidth
              type="number"
              inputProps={{
                min: 0,
                max: 60,
              }}
              disabled
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              type="number"
              label={labels.kgsBulk}
              name="kgs"
              value={kgs}
              variant="outlined"
              fullWidth
              inputProps={{
                min: 0,
              }}
              disabled
            />
          </Grid>
        </>
      ) : null}
      {idUnit?.type?.includes(unitTypes.e) ? (
        <>
          <Grid item xs={12}>
            <TextField
              label={labels.lts}
              type="number"
              name="lts"
              value={lts}
              onChange={handleInputOnChange}
              variant="outlined"
              fullWidth
              disabled={readOnly}
              onFocus={() => resetOnClick(handleInputOnChange, lts, "lts")}
            />
          </Grid>
        </>
      ) : null}

      {loadingDetails ? (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      ) : (
        !loadingDetails &&
        successDetails && (
          <>
            <Grid item xs={12} md={4}>
              {idAccount?.idAccount &&
                idUnit?.type?.includes(unitTypes.e) ? (
                <TextField
                  type="number"
                  label={labels.unitPriceLt}
                  variant="outlined"
                  fullWidth
                  value={readOnly ? inputValues.unitPrice : unitPriceLt}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              ) : (
                <TextField
                  type="number"
                  label={labels.unitPriceKg}
                  variant="outlined"
                  fullWidth
                  value={readOnly ? inputValues.unitPrice : unitPriceKg}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={labels.totalByProduct}
                name="amountDetail"
                value={totalByProduct}
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  inputProps: { min: 0, right: "1" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={labels.withholding}
                name="withholding"
                value={getDecimalFormat(withholding)}
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  inputProps: { min: 0, right: "1" },
                }}
              />
            </Grid>
          </>
        )
      )}
      <Grid item xs={12}>
        <Autocomplete
          id="liquidation-paymethods-options"
          options={paymentsOpt}
          value={idPayment}
          name="idPayment"
          defaultValue={payments.find((py) => py.label.includes(payment.efectivo))}
          getOptionLabel={(option) => option.label || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              label={labels.paymentMethod}
              variant="outlined"
              error={fieldErrors.idPayment}
              helperText={fieldErrors.idPayment || ""}
            />
          )}
          fullWidth
          onChange={(e, v) => handleMethodPayment(e, v)}
          noOptionsText={noOptions.paymentMethod}
          disabled={readOnly}
        />
      </Grid>

      {idAccount?.idAccount || readOnly ? (
        <>
          <Grid item xs={12} md={4}>
            <TextField
              label={labels.discount}
              variant="outlined"
              fullWidth
              value={formatNumber(discount)}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                inputProps: { min: 0, right: "1" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={labels.liqTotal}
              variant="outlined"
              fullWidth
              value={formatNumber(total)}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">{labels.currency}</InputAdornment>
                ),
                inputProps: { min: 0, right: "1" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={labels.retTotal}
              variant="outlined"
              fullWidth
              value={getDecimalFormat(parseFloat(total) + parseFloat(withholding))}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">{labels.currency}</InputAdornment>
                ),
                inputProps: { min: 0, right: "1" },
              }}
            />
          </Grid>
        </>
      ) : null}
      <Grid item xs={12}>
        {!validation ? (
          <Autocomplete
            id="liquidation-sgc-options"
            options={sgc || []}
            value={sgcSelected}
            getOptionLabel={(option) => option?.label}
            getOptionSelected={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                name="sgc"
                label={labels.sgcSelect}
                variant="outlined"
                error={fieldErrors.sgcSelected}
                helperText={fieldErrors.sgcSelected || ""}
              />
            )}
            fullWidth
            onChange={(e, v) => handleSgc(v)}
            noOptionsText={noOptions.sgc}
            disabled={
              (idPayment?.label?.includes(payment.condon) &&
                liquidation?.gas_bonus_auth == 0) ||
              condonationVal
            }
            style={{
              display:
                idPayment?.label?.includes(payment.condon) && isNew
                  ? "none"
                  : "block",
            }}
          />
        ) : (
          <TextField
            type="text"
            label={labels.sgc}
            variant="outlined"
            fullWidth
            value={liquidation?.fillingOrder[0]?.fillingStation}
            disabled
          />
        )}
        {condonationVal && (
          <Typography variant="body1" style={{ color: red[500], marginTop: 8 }}>
            {messages.pendingBonuses}
          </Typography>
        )}
      </Grid>
      <Grid
        item
        xs={12}
        style={{
          marginTop: 30,
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <RegularButton
          color="danger"
          onClick={onClose}
          disabled={isLoadingComp?.loadingCreateSaleStation}
        >
          {readOnly ? buttons.close : buttons.cancel}
        </RegularButton>
        {isSuccess && !liquidation.fillingOrder ? (
          <RegularButton
            color="info"
            onClick={
              liquidation.status === liquidationStatus.value.READY
                ? handleSgcSubmit
                : handleSubmit
            }
            disabled={
              isLoadingComp?.loadingCreateSaleStation ||
              (liquidation.status === liquidationStatus.value.READY &&
                !sgcSelected) ||
              condonationVal ||
              blockCredit
            }
            loading={isLoadingComp?.loadingCreateSaleStation}
          >
            {buttons.confirm}
          </RegularButton>
        ) : null}
      </Grid>
    </Grid>
  );
};
